import {
  addUserFile,
  addVectorStoreFile,
  cancelVectorStoreEmbedding,
  createVectorStore,
  deleteUserFileById,
  deleteVectorStore,
  getUserFileContent,
  getVectorStoresWithFiles,
  listUserFiles,
  listVectorStoreFiles,
  searchLocalCollection, // Added for local collection search
  listLocalCollections, // Added for listing local collections
  getVectorStoreFilesDetailed
} from '@/utils/api'
import { isApiError } from '@/types/api'
import { EmbeddingCollection, EmbeddingSearchResult, StaticChunkStrategy, UserFile, VectorStoreFile, VectorStoreWithFiles } from '@/types/embeddings' // Added EmbeddingSearchResult and EmbeddingCollection
import { Commit, Dispatch } from 'vuex'
import { connectSSE, disconnectSSE } from '@/utils/sse'

export interface EmbeddingsState {
  userFiles: Record<string, UserFile>
  userFileContents: Record<string, string>
  vectorStores: Record<string, VectorStoreWithFiles> // These are OpenAI Vector Stores
  vectorStoreFiles: Record<string, VectorStoreFile>
  searchResults: EmbeddingSearchResult[]
  localCollections: EmbeddingCollection[] // Added for MissionSquadCore local collections
  isLoadingVectorStores: boolean
  isLoadingLocalCollections: boolean
  lastCollectionsRefresh: number | null
}

export const embeddings = () => ({
  state: <EmbeddingsState>{
    userFiles: {},
    userFileContents: {},
    vectorStores: {},
    vectorStoreFiles: {},
    searchResults: [],
    localCollections: [],
    isLoadingVectorStores: false,
    isLoadingLocalCollections: false,
    lastCollectionsRefresh: null
  },
  mutations: {
    setUserFiles(state: EmbeddingsState, userFiles: Record<string, UserFile>) {
      state.userFiles = userFiles
    },
    setLocalCollections(state: EmbeddingsState, collections: EmbeddingCollection[]) { // Added mutation
      state.localCollections = collections
    },
    setSearchResults(state: EmbeddingsState, results: EmbeddingSearchResult[]) {
      state.searchResults = results
    },
    setUserFileContents(state: EmbeddingsState, userFileContents: Record<string, string>) {
      state.userFileContents = userFileContents
    },
    setVectorStores(state: EmbeddingsState, vectorStores: Record<string, VectorStoreWithFiles>) {
      state.vectorStores = vectorStores
    },
    setVectorStoreFiles(state: EmbeddingsState, vectorStoreFiles: Record<string, VectorStoreFile>) {
      state.vectorStoreFiles = vectorStoreFiles
    },
    addUserFile(state: EmbeddingsState, userFiles: UserFile[]) {
      userFiles.forEach((userFile) => {
        state.userFiles[userFile.id] = userFile
      })
    },
    addUserFileContent(state: EmbeddingsState, userFileContent: { id: string; content: string }) {
      state.userFileContents[userFileContent.id] = userFileContent.content
    },
    addVectorStore(state: EmbeddingsState, vectorStore: VectorStoreWithFiles) {
      if (vectorStore.files === null) {
        vectorStore.files = []
      }
      state.vectorStores[vectorStore.id] = vectorStore
    },
    addVectorStoreFile(state: EmbeddingsState, vectorStoreFile: VectorStoreFile) {
      state.vectorStoreFiles[vectorStoreFile.id] = vectorStoreFile
    },
    removeUserFile(state: EmbeddingsState, userFileId: string) {
      delete state.userFiles[userFileId]
    },
    removeUserFileContent(state: EmbeddingsState, userFileContentId: string) {
      delete state.userFileContents[userFileContentId]
    },
    removeVectorStore(state: EmbeddingsState, vectorStoreId: string) {
      delete state.vectorStores[vectorStoreId]
    },
    removeVectorStoreFile(state: EmbeddingsState, vectorStoreFileId: string) {
      delete state.vectorStoreFiles[vectorStoreFileId]
    },
    setVectorStoresLoading(state: EmbeddingsState, isLoading: boolean) {
      state.isLoadingVectorStores = isLoading
    },
    setLocalCollectionsLoading(state: EmbeddingsState, isLoading: boolean) {
      state.isLoadingLocalCollections = isLoading
    },
    setLastCollectionsRefresh(state: EmbeddingsState, timestamp: number | null) {
      state.lastCollectionsRefresh = timestamp
    }
  },
  actions: {
    async listUserFiles({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      const userFiles = await listUserFiles()

      if (isApiError(userFiles)) {
        dispatch('updateSnackbar', { text: `List User Files Error: ${userFiles.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'List User Files Error', content: userFiles }, { root: true })
        return
      }

      const { data } = userFiles
      commit('setUserFiles', data)
      dispatch(
        'addLogEvent',
        {
          title: 'User Files Listed',
          content: { fileCount: Object.keys(data).length }
        },
        { root: true }
      )
    },

    async fetchUserFileContent({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, userFileId: string) {
      const userFileContent = await getUserFileContent(userFileId)

      if (isApiError(userFileContent)) {
        dispatch('updateSnackbar', { text: `Fetch File Content Error: ${userFileContent.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Fetch File Content Error', content: userFileContent }, { root: true })
        return null
      }

      commit('addUserFileContent', { id: userFileId, content: userFileContent })
      dispatch(
        'addLogEvent',
        {
          title: 'User File Content Fetched',
          content: { fileId: userFileId }
        },
        { root: true }
      )
      return userFileContent
    },

    async addUserFile(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      {
        file,
        purpose = 'assistants',
        relativePath,
        collectionName
      }: { file: File; purpose: string; relativePath?: string; collectionName?: string }
    ) {
      const response = await addUserFile(file, purpose, relativePath, collectionName)

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Add User File Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Add User File Error', content: response }, { root: true })
        return null
      }

      // Ensure we're working with an array before passing to the mutation
      const filesArray = Array.isArray(response) ? response : [response]
      commit('addUserFile', filesArray)
      dispatch(
        'addLogEvent',
        {
          title: 'User File Added',
          content: {
            fileName: file.name,
            purpose: purpose,
            fileCount: filesArray.length,
            relativePath: relativePath,
            collectionName: collectionName
          }
        },
        { root: true }
      )
      return filesArray
    },

    async deleteUserFile({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, userFileId: string) {
      const response = await deleteUserFileById(userFileId)

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Delete User File Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Delete User File Error', content: response }, { root: true })
        return
      }

      commit('removeUserFile', userFileId)
      commit('removeUserFileContent', userFileId)
      dispatch('addLogEvent', { title: 'User File Deleted', content: userFileId }, { root: true })
    },
    // async listVectorStores({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
    //   try {
    //     const vectorStoresResponse = await listVectorStores()
    //     if (vectorStoresResponse != null) {
    //       const { data } = vectorStoresResponse
    //       const vectorStores: Record<string, VectorStore> = data.reduce((acc: Record<string, VectorStore>, store: VectorStore) => {
    //         acc[store.id] = store
    //         return acc
    //       }, <Record<string, VectorStore>>{})
    //       commit('setVectorStores', vectorStores)
    // dispatch('addLogEvent', {
    //   title: 'Vector Stores Listed',
    //   content: { storeCount: Object.keys(vectorStores).length }
    // }, { root: true })
    //     }
    //   } catch (error) {
    //     console.error('Error listing vector stores:', error)
    //   }
    // },
    async listVectorStores({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      commit('setVectorStoresLoading', true)
      try {
        const vectorStoresResponse = await getVectorStoresWithFiles()

        if (isApiError(vectorStoresResponse)) {
          dispatch(
            'updateSnackbar',
            { text: `List Vector Stores Error: ${vectorStoresResponse.message}` },
            { root: true }
          )
          dispatch('addLogEvent', { title: 'List Vector Stores Error', content: vectorStoresResponse }, { root: true })
          return
        }

        const { data } = vectorStoresResponse
        const vectorStores: Record<string, VectorStoreWithFiles> = data.reduce(
          (acc: Record<string, VectorStoreWithFiles>, store: VectorStoreWithFiles) => {
            acc[store.id] = store
            return acc
          },
          {} as Record<string, VectorStoreWithFiles>
        )
        commit('setVectorStores', vectorStores)
        commit('setLastCollectionsRefresh', Date.now())
      } finally {
        commit('setVectorStoresLoading', false)
      }
    },

    async fetchVectorStoreFiles(
      { commit, dispatch, state }: { commit: Commit; dispatch: Dispatch; state: EmbeddingsState },
      vectorStoreId: string
    ) {
      try {
        const response = await getVectorStoreFilesDetailed(vectorStoreId)

        if (isApiError(response)) {
          dispatch('updateSnackbar', { text: `Load Files Error: ${response.message}` }, { root: true })
          return null
        }

        // Update the specific vector store with its files
        const currentStore = state.vectorStores[vectorStoreId]
        if (currentStore) {
          commit('addVectorStore', {
            ...currentStore,
            files: response.files
          })
        }

        return response.files
      } catch (error) {
        console.error('Error fetching vector store files:', error)
        dispatch('updateSnackbar', { text: 'Failed to load collection files' }, { root: true })
        return null
      }
    },

    async createVectorStore(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      {
        name,
        file_ids,
        embeddingModelName,
        chunking_strategy,
        sseSessionId,
        enhancePDF
      }: {
        name: string
        file_ids: string[]
        embeddingModelName?: string
        chunking_strategy?: StaticChunkStrategy
        sseSessionId?: string
        enhancePDF?: boolean
      }
    ) {
      const vectorStore = await createVectorStore(
        name,
        file_ids,
        embeddingModelName,
        chunking_strategy,
        sseSessionId,
        enhancePDF
      )

      if (isApiError(vectorStore)) {
        dispatch('updateSnackbar', { text: `Create Vector Store Error: ${vectorStore.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Create Vector Store Error', content: vectorStore }, { root: true })
        return null
      }

      commit('addVectorStore', vectorStore)
      dispatch(
        'addLogEvent',
        {
          title: 'Vector Store Created',
          content: {
            name: name,
            id: vectorStore.id,
            fileCount: file_ids.length,
            embeddingModel: embeddingModelName
          }
        },
        { root: true }
      )
      return vectorStore
    },

    connectSSE({ dispatch }: { dispatch: Dispatch }, sessionId: string) {
      connectSSE(sessionId, (event) => {
        dispatch('handleSSEEvent', event)
      })
    },

    disconnectSSE() {
      disconnectSSE()
    },

    handleSSEEvent({ commit }: { commit: Commit }, event: any) {
      // Create a custom event to be dispatched globally
      const customEvent = new CustomEvent('sse-event', { detail: event })
      window.dispatchEvent(customEvent)
    },

    async deleteVectorStore({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, vectorStoreId: string) {
      const response = await deleteVectorStore(vectorStoreId)

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Delete Vector Store Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Delete Vector Store Error', content: response }, { root: true })
        return
      }

      commit('removeVectorStore', vectorStoreId)
      dispatch('addLogEvent', { title: 'Vector Store Deleted', content: vectorStoreId }, { root: true })
    },

    async cancelVectorStoreEmbedding(
      { dispatch }: { dispatch: Dispatch },
      { sessionId }: { sessionId: string }
    ) {
      const response = await cancelVectorStoreEmbedding(sessionId)
      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Cancel Embedding Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Cancel Embedding Error', content: response }, { root: true })
      } else {
        dispatch('updateSnackbar', { text: 'Cancellation request sent.' }, { root: true })
      }
    },

    async listVectorStoreFiles({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, vectorStoreId: string) {
      const response = await listVectorStoreFiles(vectorStoreId)

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `List Vector Store Files Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'List Vector Store Files Error', content: response }, { root: true })
        return
      }

      const { data } = response
      const files = data.reduce((acc: Record<string, VectorStoreFile>, item: VectorStoreFile) => {
        acc[item.id] = item
        return acc
      }, {} as Record<string, VectorStoreFile>)
      commit('setVectorStoreFiles', { vectorStoreId, files })
      dispatch(
        'addLogEvent',
        {
          title: 'Vector Store Files Listed',
          content: {
            vectorStoreId: vectorStoreId,
            fileCount: Object.keys(files).length
          }
        },
        { root: true }
      )
    },

    async addVectorStoreFile(
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      {
        vectorStoreId,
        fileId,
        chunkStrategy
      }: { vectorStoreId: string; fileId: string; chunkStrategy?: StaticChunkStrategy }
    ) {
      const response = await addVectorStoreFile(vectorStoreId, fileId, chunkStrategy)

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Add Vector Store File Error: ${response.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Add Vector Store File Error', content: response }, { root: true })
        return
      }

      commit('addVectorStoreFile', response)
      dispatch(
        'addLogEvent',
        {
          title: 'Vector Store File Added',
          content: {
            vectorStoreId: vectorStoreId,
            fileId: fileId
          }
        },
        { root: true }
      )
    },
    // async deleteVectorStoreFile({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, { vectorStoreId, fileId }: { vectorStoreId: string, fileId: string }) {
    //   try {
    //     const response = await deleteVectorStoreFile(vectorStoreId, fileId)
    //     if (response != null) {
    //       commit('removeVectorStoreFile', { vectorStoreId, fileId })
    //     }
    //   } catch (error) {
    //     console.error('Error deleting vector store file:', error)
    //   }
    // }
    async searchLocalCollectionAction( // Added action
      { commit, dispatch }: { commit: Commit; dispatch: Dispatch },
      { collectionName, query, embeddingModelName, topK }: { collectionName: string; query: string; embeddingModelName: string, topK?: number }
    ) {
      const response = await searchLocalCollection(collectionName, query, embeddingModelName, topK)

      if (isApiError(response)) {
        dispatch('updateSnackbar', { text: `Search Error: ${response.message}` }, { root: true })
        commit('setSearchResults', []) // Clear previous results on error
        return null
      }

      if (response.success && response.results) {
        commit('setSearchResults', response.results)
        // Optional: dispatch snackbar for success, e.g., number of results
        // dispatch('updateSnackbar', { text: `Search returned ${response.results.length} results.` }, { root: true });
        return response.results
      } else {
        // Handle cases where success is true but results might be empty (valid)
        // or success is false with a message
        if (response.success && !response.results) { // Successful search, no results
          commit('setSearchResults', [])
          // dispatch('updateSnackbar', { text: 'No results found.' }, { root: true });
        } else { // Search failed as per API response
          dispatch('updateSnackbar', { text: response.message || 'Search failed.' }, { root: true })
          commit('setSearchResults', [])
        }
        return null
      }
    },
    async fetchLocalCollections({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      // Added action
      commit('setLocalCollectionsLoading', true)
      try {
        const response = await listLocalCollections()

        if (isApiError(response)) {
          dispatch('updateSnackbar', { text: `List Local Collections Error: ${response.message}` }, { root: true })
          commit('setLocalCollections', [])
          return
        }
        if (response.success && response.data) {
          commit('setLocalCollections', response.data)
          commit('setLastCollectionsRefresh', Date.now())
        } else {
          commit('setLocalCollections', [])
        }
      } finally {
        commit('setLocalCollectionsLoading', false)
      }
    }
  },
  getters: {
    userFiles: (state: EmbeddingsState) => state.userFiles,
    fileContents: (state: EmbeddingsState) => state.userFileContents,
    vectorStores: (state: EmbeddingsState) => state.vectorStores, // These are OpenAI Vector Stores
    vectorStoreFiles: (state: EmbeddingsState) => state.vectorStoreFiles,
    searchResults: (state: EmbeddingsState) => state.searchResults,
    localCollections: (state: EmbeddingsState) => state.localCollections, // Added getter
    isLoadingVectorStores: (state: EmbeddingsState) => state.isLoadingVectorStores,
    isLoadingLocalCollections: (state: EmbeddingsState) => state.isLoadingLocalCollections,
    isLoadingCollections: (state: EmbeddingsState) => state.isLoadingVectorStores || state.isLoadingLocalCollections,
    lastCollectionsRefresh: (state: EmbeddingsState) => state.lastCollectionsRefresh
  }
})
